'use client';

import { memo, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { cn } from '../utils/cn';
import { rarityGradient } from '../utils/rarityGradient';
import { DropSourceIcon } from './DropSourceIcon';
import { SkinImage } from './ui/SkinImage';
import { parseItemDetails, abbreviateWearCondition } from '../utils/formatters';

export const ITEM_CARD_HEIGHT = 220;
export const ITEM_GRID_GAP = 4;
export const ITEM_GRID_MIN_COLUMN = 160;

export interface ItemListItemProps {
  item: {
    name: string;
    imageUrl: string | null;
    rarityColor?: string | null;
    source?: string;
  };
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
  topRightContent?: ReactNode;
  bottomRightContent?: ReactNode;
  bottomLeftContent?: ReactNode;
  hideCondition?: boolean;
  hideBottomInfo?: boolean;
  hideWeaponName?: boolean;
  image?: ReactNode;
  labels?: { top?: ReactNode; main: ReactNode };
  neutralBackground?: boolean;
  scrim?: boolean;
}

const ItemListItemComponent: FC<ItemListItemProps> = ({ item, onClick, className, children, topRightContent, bottomRightContent, bottomLeftContent, hideCondition, hideBottomInfo, hideWeaponName, image, labels, neutralBackground, scrim }) => {
  const { weaponName, skinName, condition, isStar, isStatTrak, isSouvenir } = useMemo(
    () => parseItemDetails(item.name),
    [item.name]
  );

  const isGraffitiOrSticker = weaponName === 'Graffiti' || weaponName === 'Sticker';

  const backgroundStyle = useMemo(
    () => (neutralBackground ? undefined : { background: rarityGradient(item.rarityColor) }),
    [item.rarityColor, neutralBackground]
  );

  return (
    <div
      className={cn('relative w-full h-[220px] rounded-[21px] overflow-hidden cursor-pointer text-white', className)}
      onClick={onClick}
      style={backgroundStyle}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
      />
      <div className="w-full h-full absolute top-0 start-0 flex items-center justify-center">
        {image ?? (
          <SkinImage
            name={item.name}
            src={item.imageUrl}
            imgClassName="w-full h-full object-contain object-[center_35%] [filter:drop-shadow(0_8px_16px_rgba(0,0,0,0.55))]"
          />
        )}
      </div>

      {!labels && !hideCondition && (isStar || isStatTrak || isSouvenir || condition) && (
        // WHY: dir только на внутренней строке — start-2 считается от собственного direction, ltr здесь вернёт бейдж в левый угол
        <div className="absolute top-2 start-2 text-xs font-semibold uppercase tracking-[0.5px] [text-shadow:0_2px_4px_rgba(0,0,0,0.8)] z-[2] flex items-center">

          <span dir="ltr" className="flex items-center">
            {(isStar || isStatTrak || isSouvenir) && (
              <span className="text-price">
                {isStar && '★'}
                {isStar && (isStatTrak || isSouvenir) && ' '}
                {isStatTrak && 'StatTrak™'}
                {isStatTrak && isSouvenir && ' '}
                {isSouvenir && 'Souvenir'}
              </span>
            )}
            {(isStar || isStatTrak || isSouvenir) && condition && <span className="text-price"> / </span>}
            {condition && (
              <span className="text-white">
                {isGraffitiOrSticker ? condition : abbreviateWearCondition(condition)}
              </span>
            )}
          </span>
        </div>
      )}

      {(topRightContent || item.source) && (
        <div className="absolute top-2 end-2 text-sm font-semibold text-white [text-shadow:0_2px_4px_rgba(0,0,0,0.8)] z-[2] flex items-center gap-1.5">
          {item.source && <DropSourceIcon source={item.source} size="medium" />}
          {topRightContent}
        </div>
      )}

      {!hideBottomInfo && (
        <div className={cn('absolute bottom-0 start-0 end-0 flex flex-col items-start p-3 gap-1 z-[2]', scrim && 'bg-gradient-to-t from-black/55 to-transparent pt-8')}>
          <div className="flex flex-col items-start gap-0.5 w-full">
            {labels?.top && <div className="w-full text-start text-sm font-medium text-white [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">{labels.top}</div>}
            {labels && <div className="w-full text-start text-base font-semibold text-white [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">{labels.main}</div>}
            {!labels && !hideWeaponName && <div className="text-sm font-medium text-white m-0 truncate w-full text-start [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">{weaponName}</div>}
            <div className="flex flex-col items-start gap-0.5 w-full">
              {!labels && skinName && (
                <div className="text-base font-semibold text-white truncate w-full text-start [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">{skinName}</div>
              )}
            </div>
          </div>
          {children && <div className="flex flex-col items-start gap-1 w-full">{children}</div>}
        </div>
      )}

      {bottomLeftContent && (
        <div className="absolute bottom-2 start-2 z-[3]">
          {bottomLeftContent}
        </div>
      )}

      {bottomRightContent && (
        <div className="absolute bottom-2 end-2 z-[3]">
          {bottomRightContent}
        </div>
      )}
    </div>
  );
};

export const ItemListItem = memo(ItemListItemComponent);
